import { QueryOptionsFactory } from "@/features/company/utils";
import {
  AliasMobileSchema,
  CreateAliasMobileData,
  DeleteAliasMobileData
} from "@/types/user/alias-mobile.types";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";
import { AuthApiClient } from "../api-client/api-client";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";

export const useAliasMobile = () => {
  const queryClient = useQueryClient();
  const { leUuid: legalEntityUuid } = useCompanyContext();

  // GET all alias mobile from real //
  const useGetAliasMobile = (options?: Omit<UseQueryOptions<AliasMobileSchema[]>, "queryKey">) =>
    useQuery<AliasMobileSchema[]>({
      ...QueryOptionsFactory.aliasMobile(legalEntityUuid),
      ...options,
      queryFn: () => AuthApiClient.getAllAliasMobile(legalEntityUuid)
    });

  // DELETE alias mobile from real //
  const useDeleteAliasMobile = (
    options?: UseMutationOptions<DeleteAliasMobileData, Error, DeleteAliasMobileData>
  ) => {
    return useMutation<DeleteAliasMobileData, Error, DeleteAliasMobileData>({
      mutationFn: async (data: DeleteAliasMobileData) =>
        await AuthApiClient.deleteAliasMobile(data.uuid, legalEntityUuid),
      onSuccess: (response: DeleteAliasMobileData) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMobile(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (oldData: AliasMobileSchema[] | undefined) => {
          return oldData?.filter((element) => element.uuid !== response.uuid);
        });
      },
      ...options
    });
  };

  // POST alias mobile to real //
  const useCreateAliasMobile = (
    options?: UseMutationOptions<AliasMobileSchema, Error, CreateAliasMobileData>
  ) => {
    return useMutation<AliasMobileSchema, Error, CreateAliasMobileData>({
      mutationFn: async (aliasMobile: CreateAliasMobileData) =>
        await AuthApiClient.createAliasMobile(aliasMobile, legalEntityUuid),
      onSuccess: (response: AliasMobileSchema) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMobile(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (queryData: AliasMobileSchema[] | undefined) => {
          if (queryData) {
            if (response.main) {
              queryData.map((element) => (element.main = false));
            }
            queryData.push(response);
          }
          return queryData;
        });
      },
      ...options
    });
  };

  // PATCH alias mobile public //
  type SetPublicAliasMobileBody = {
    aliasMobileUuid: string;
    public: boolean;
  };
  const useSetPublicAliasMobile = (
    options?: UseMutationOptions<AliasMobileSchema, Error, SetPublicAliasMobileBody>
  ) => {
    return useMutation<AliasMobileSchema, Error, SetPublicAliasMobileBody>({
      mutationFn: async (data: SetPublicAliasMobileBody) =>
        await AuthApiClient.setPublicAliasMobile(
          data.aliasMobileUuid,
          legalEntityUuid,
          data.public
        ),
      onSuccess: (response: AliasMobileSchema) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMobile(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (queryData: AliasMobileSchema[] | undefined) => {
          if (!queryData) return undefined;
          for (const aliasMobile of queryData) {
            if (aliasMobile.uuid === response.uuid) {
              aliasMobile.public = response.public;
            } else {
              aliasMobile.public = false;
            }
          }
          return queryData;
        });
      },
      ...options
    });
  };

  return { useGetAliasMobile, useDeleteAliasMobile, useCreateAliasMobile, useSetPublicAliasMobile };
};
